import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import GlobalConfig from "../context/GlobalConfig";
import { languageData } from "../constants/languageData";
import Footer from "../components/Footer";
import GoogleIcon from "../components/GoogleIcon";
import Head from "./Head";

function CameraAccessDenied({ stepperFunc, isMobile, steps, deviceContext }) {
  const { config, routesAndNavigation } = useContext(GlobalConfig);
  const { browser = 'Unknown' } = deviceContext || {};
  const browserKeyMap = {
    "Android WebView": "androidWebViewPermissionDenied",
    "Android Browser": "androidBrowserPermissionDenied",
    "iOS WebView": "iosWebViewPermissionDenied",
    "iOS Browser": "iosBrowserPermissionDenied",
    "Desktop Browser": "desktopBrowserPermissionDenied",
    "Unknown": "permissionDenied",
  };
  const browserTypeKey = browserKeyMap[browser] || "permissionDenied";

  const language = sessionStorage.getItem("language") ?? "en";
  const handleLinkClick = () => {
    const userAgent = navigator.userAgent;
    let supportUrl;

    if (userAgent.includes("Chrome") && !userAgent.includes("Edg/")) {
      // This ensures it's Chrome, not Edge
      supportUrl =
        "https://support.google.com/chrome/answer/2693767?hl=en&co=GENIE.Platform%3DDesktop&oco=0";
    } else if (userAgent.includes("Edg/")) {
      // Edge specifically
      supportUrl =
        "https://help.doxy.me/en/articles/3867526-camera-and-microphone-permission-edge";
    } else if (userAgent.includes("Firefox")) {
      supportUrl =
        "https://help.doxy.me/en/articles/2427185-camera-and-microphone-permission-firefox";
    } else if (userAgent.includes("Safari") && !userAgent.includes("Chrome")) {
      supportUrl =
        "https://help.doxy.me/en/articles/836274-camera-and-microphone-permission-safari";
    } else {
      // Default Chrome link
      supportUrl =
        "https://support.google.com/chrome/answer/2693767?hl=en&co=GENIE.Platform%3DDesktop&oco=0";
    }

    window.open(supportUrl, "_blank");
  };

  async function requestCameraPermission() {
    console.log('[CameraAccessDenied] Starting camera permission flow');
  
    // 1. Check browser support
    if (!navigator.mediaDevices) {
      console.error('[CameraAccessDenied] navigator.mediaDevices not available - likely insecure context (non-HTTPS)');
      alert(
        languageData[language]?.cameraAccessError?.alert3 ??
        languageData["en"]?.cameraAccessError?.alert3
      );
      return;
    }
  
    if (!navigator.mediaDevices.getUserMedia) {
      console.error('[CameraAccessDenied] getUserMedia not supported in this browser');
      alert(
        languageData[language]?.cameraAccessError?.alert3 ??
        languageData["en"]?.cameraAccessError?.alert3
      );
      return;
    }
  
    // 2. Check existing permissions
    let permissionGranted = false;
    
    if (navigator.permissions?.query) {
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'camera' });
        permissionGranted = permissionStatus.state === 'granted' || sessionStorage.getItem('cameraAccessAllowed') === 'true';
        console.log(`[CameraAccessDenied] Permissions API state: ${permissionStatus?.state}`);
        if(permissionGranted) {
          console.log('[CameraAccessDenied] Permissions Already granted . No need to request for permission again.');
        }
      } catch (e) {
        console.log('[CameraAccessDenied] Permissions API not fully supported, falling back to sessionStorage');
        permissionGranted = sessionStorage.getItem('cameraAccessAllowed') === 'true';
      }
    } else {
      console.log('[CameraAccessDenied] Permissions API not available, falling back to sessionStorage');
      permissionGranted = sessionStorage.getItem('cameraAccessAllowed') === 'true';
    }
  
    // 3. Request camera only if needed
    if (!permissionGranted) {
      console.log('[CameraAccessDenied] Permission not granted - requesting access');
      
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: isMobile ? { min: 720, ideal: 1920 } : { min: 720, ideal: 1280 },
            height: isMobile ? { min: 720, ideal: 1080 } : { min: 720, ideal: 720 },
            facingMode: isMobile ? { ideal: "environment" } : { ideal: "user" },
          },
        });
  
        console.log('[CameraAccessDenied] Camera access granted, stream obtained');
        
        // Log and release tracks
        const tracks = stream.getTracks();
        console.log(`[CameraAccessDenied] Found ${tracks.length} active tracks to stop`);
        
        tracks.forEach(track => {
          console.log(`[CameraAccessDenied] Stopping ${track.kind} track (ID: ${track.id})`);
          console.log('Track details:', {
            readyState: track.readyState,
            enabled: track.enabled,
            muted: track.muted
          });
          
          track.stop();
          console.log("[CameraAccessDenied] Track stopped, new state:", track.readyState);
        });
  
        // Remember permission for future
        sessionStorage.setItem('cameraAccessAllowed', 'true');
        console.log('[CameraAccessDenied] Saved permission state in sessionStorage');
        
      } catch (error) {
        console.error('[CameraAccessDenied] Camera access error:', {
          name: error?.name,
          message: error?.message,
          constraints: error?.constraints,
          fullError: error
        });
  
        if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
          console.warn('[CameraAccessDenied] User denied camera permission');
          alert(
            languageData[language]?.cameraAccessError?.alert1 ||
            languageData["en"]?.cameraAccessError?.alert1 || "Camera access was denied. Please enable camera access in your browser settings"
          );
        } 
        else if (error.name === "NotFoundError") {
          console.error('[CameraAccessDenied] No camera device found');
          alert(
            languageData[language]?.cameraAccessError?.alert2 ??
            languageData["en"]?.cameraAccessError?.alert2
          );
        }
        else if (error.name === "OverconstrainedError") {
          console.error('[CameraAccessDenied] Camera constraints could not be met:', error?.constraint);
          alert(
            languageData[language]?.cameraAccessError?.alert2 ??
            languageData["en"]?.cameraAccessError?.alert2
          );
        }
        else if (error.name === "NotReadableError") {
          console.error('[CameraAccessDenied] Camera is already in use by another application');
          alert(
            languageData[language]?.cameraAccessError?.alert2 ??
            languageData["en"]?.cameraAccessError?.alert2
          );
        }
        else {
          console.error('[CameraAccessDenied] Unknown camera error:', error);
          alert(
            languageData[language]?.cameraAccessError?.alert2 ??
            languageData["en"]?.cameraAccessError?.alert2
          );
        }
        window.location.reload();
        return;
      }
    } else {
      console.log('[CameraAccessDenied] Using previously granted camera permission');
    }
  
    // 4. Proceed with application flow
    console.log('[CameraAccessDenied] All camera checks complete, proceeding to next step');
    stepperFunc(steps[0]);
  }

  return (
    <>
      <Head
        style={{ zIndex: "100", position: "relative", top: 0 }}
        showLanguages={false}
        showHelp={true}
        showMobileFlow={false}
        stepperFunc={stepperFunc}
      />
      <div className="parent-container" style={{ paddingTop: 0 }}>
        <div className="main">
          <div className="left-section">
            <GoogleIcon iconName="videocam_off" size={48} style={{ color: "#656565" }}/>
          </div>
          <div
            className="right-section md:items-start items-center"
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              fontFamily: "Inter, sans-serif",
            }}
          >
            <div className="flex flex-col h-full">
              <div className="text-[18px] md:text-[28px] font-semibold text-[#000000] text-center md:text-left">
                {languageData[language]?.[browserTypeKey]?.title ??
                  languageData["en"]?.[browserTypeKey]?.title}
              </div>
              {/* <div className="mt-[36px] mb-4 text-[12px] md:text-[14px] text-[#656565] text-center md:text-left  font-inter text-sm font-semibold leading-normal">
                {languageData[language]?.permissionDenied
                  ?.subtitle2 ?? languageData["en"]?.permissionDenied?.subtitle2}
              </div> */}
              <div className="mt-[36px] mb-4 text-[12px] md:text-[14px] text-[#656565] text-center md:text-left  font-inter text-sm font-semibold leading-normal">
                {languageData[language]?.[browserTypeKey]?.subtitle ??
                  languageData["en"]?.[browserTypeKey]?.subtitle}
                <button
                  onClick={handleLinkClick}
                  style={{
                    color: "#1658aa", // Dynamic text color
                    textDecoration: "none", // Remove underline
                    background: "none", // Remove button background
                    border: "none", // Remove border
                    padding: "0", // Remove padding to mimic a link
                    cursor: "pointer", // Pointer cursor to indicate clickability
                    textAlign: "inherit", // Inherit text alignment
                    display: "inline-flex", // Use inline-flex to make them inline and allow flex properties
                    alignItems: "center", // Vertically align text and icon in the center
                  }}
                  className="font-inter text-sm font-semibold leading-normal justify-center md:justify-start"
                >
                  {languageData[language]?.[browserTypeKey]?.readMore && (
                    <>
                      {languageData[language]?.[browserTypeKey]?.readMore}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="#1658aa"
                        className="ml-2"
                      >
                        <g clipPath="url(#clip0_5376_103561)">
                          <path
                            d="M10.5 11.0833H3.5C3.17917 11.0833 2.91667 10.8208 2.91667 10.5V3.5C2.91667 3.17917 3.17917 2.91667 3.5 2.91667H6.41667C6.7375 2.91667 7 2.65417 7 2.33333C7 2.0125 6.7375 1.75 6.41667 1.75H2.91667C2.26917 1.75 1.75 2.275 1.75 2.91667V11.0833C1.75 11.725 2.275 12.25 2.91667 12.25H11.0833C11.725 12.25 12.25 11.725 12.25 11.0833V7.58333C12.25 7.2625 11.9875 7 11.6667 7C11.3458 7 11.0833 7.2625 11.0833 7.58333V10.5C11.0833 10.8208 10.8208 11.0833 10.5 11.0833ZM8.16667 2.33333C8.16667 2.65417 8.42917 2.91667 8.75 2.91667H10.2608L4.935 8.2425C4.7075 8.47 4.7075 8.8375 4.935 9.065C5.1625 9.2925 5.53 9.2925 5.7575 9.065L11.0833 3.73917V5.25C11.0833 5.57083 11.3458 5.83333 11.6667 5.83333C11.9875 5.83333 12.25 5.57083 12.25 5.25V2.33333C12.25 2.0125 11.9875 1.75 11.6667 1.75H8.75C8.42917 1.75 8.16667 2.0125 8.16667 2.33333Z"
                            fill="#1658aa"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_5376_103561">
                            <rect width="14" height="14" fill="#1658aa" />
                          </clipPath>
                        </defs>
                      </svg>
                    </>
                  )}
                </button>
              </div>
              <div className="flex flex-col justify-center items-center text-[14px]  text-[#656565] text-center md:text-left mt-auto mb-auto">
                {languageData[language]?.[browserTypeKey]?.refreshText ??
                  languageData["en"]?.[browserTypeKey]?.refreshText}
              </div>
            </div>

            <div className=" flex flex-col justify-between font-sans w-full">
              {!isMobile && (
                <button
                  className="flex flex-row items-center justify-center gap-2 w-full text-[#1658aa] py-4 px-4 rounded underline font-semibold"
                  style={{
                    backgroundColor: "white",
                    color: config?.branding?.accentColor,
                  }}
                  onClick={() => {
                    const enableMobileFlowKey = Object.keys(
                      routesAndNavigation?.routes
                    ).find((key) =>
                      routesAndNavigation?.routes[key].includes("enable-mobile-flow")
                    );
                    if (enableMobileFlowKey) {
                      stepperFunc(enableMobileFlowKey);
                    }
                  }}
                >
                  {languageData[language]?.[browserTypeKey]?.buttonText}
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g
                      id="add_to_home_screen"
                      clip-path="url(#clip0_2274_22553)"
                    >
                      <path
                        id="Vector"
                        d="M12 0.673659L5.33333 0.666992C4.6 0.666992 4 1.26699 4 2.00033V4.00033H5.33333V3.33366H12V12.667H5.33333V12.0003H4V14.0003C4 14.7337 4.6 15.3337 5.33333 15.3337H12C12.7333 15.3337 13.3333 14.7337 13.3333 14.0003V2.00033C13.3333 1.26699 12.7333 0.673659 12 0.673659ZM6.66667 10.0003H8V5.33366H3.33333V6.66699H5.72667L2 10.3937L2.94 11.3337L6.66667 7.60699V10.0003Z"
                        fill={config?.branding?.accentColor}
                        style={{ fill: config?.branding?.accentColor }}
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_2274_22553">
                        <rect
                          width="16"
                          height="16"
                          fill={config?.branding?.accentColor}
                        />
                      </clipPath>
                    </defs>
                  </svg>
                </button>
              )}

              <button
                className="flex flex-row items-center justify-center md:gap-4 gap-6 w-full text-white py-4 px-4 rounded text-[16px] font-sans"
                style={{
                  backgroundColor: config?.branding?.accentColor,
                  marginTop: isMobile ? "12px" : "0px",
                }}
                onClick={() => requestCameraPermission()}  
              >
                {languageData[language]?.[browserTypeKey]?.refreshBtn ??
                  languageData["en"]?.[browserTypeKey]?.refreshBtn}
                <ArrowPathIcon className="h-5 w-5" />
              </button>
              <Footer style={{ marginTop: "1rem" }} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default CameraAccessDenied;
